import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import Group from 'components/utils/Group'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import React, { useMemo } from 'react'
import CellContent from './CellContent'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useOffers from 'hooks/Offers/useOffers'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { isLEHotel } from 'lib/offer/offerTypes'
import { partitionBy } from 'lib/array/arrayUtils'
import { extractPkgInclusions } from 'lib/offer/inclusionUtils'
import moment from 'moment'

interface Props {
  offer: App.HotelOffer
  filters: App.OfferListFilters
  packageUniqueKey: string
  selectionId: string
  selections: Array<App.OfferComparisonSelection>
}

function getOfferInclusions(offer: App.AnyOffer, packageUniqueKey?: string, filters?: App.OfferListFilters): Array<App.PackageInclusion> {
  if (!isLEHotel(offer) || !filters?.checkIn || !filters?.checkOut) {
    return []
  }

  const selectedPackage = packageUniqueKey ? offer.packages.find(p => p.uniqueKey === packageUniqueKey) : undefined

  const { checkIn, checkOut } = filters
  const checkInDate = !checkIn ? undefined : typeof checkIn === 'string' ? moment(checkIn) : checkIn
  const checkOutDate = !checkOut ? undefined : typeof checkOut === 'string' ? moment(checkOut) : checkOut

  const [
    inclusions,
    bonusInclusions,
    _,
    tacticalInclusions
  ] = extractPkgInclusions(selectedPackage, { checkIn: checkInDate, checkOut: checkOutDate })

  return [
    ...inclusions,
    ...bonusInclusions,
    ...tacticalInclusions,
  ]
}

function isInclusionInAllOtherOffers(
  inclusion: App.PackageInclusion,
  currentSelectionId: string,
  selections: Array<App.OfferComparisonSelection>,
  allOffers: Array<App.AnyOffer>,
): boolean {
  const otherSelections = selections.filter(s => s.selectionId !== currentSelectionId)

  return otherSelections.every(selection => {
    const otherOffer = allOffers.find(o => o.id === selection.offerId)
    if (!otherOffer) return false

    const otherInclusions = getOfferInclusions(otherOffer, selection.params?.packageUniqueKey, selection.params?.filters)
    return otherInclusions.some(otherInclusion => otherInclusion.description === inclusion.description)
  })
}

function LEHotelExclusiveInclusionsCell({
  filters,
  offer,
  packageUniqueKey,
  selectionId,
  selections,
}: Props) {
  const selectedPackage = offer.packages.find(p => p.uniqueKey === packageUniqueKey)

  const [allOffers] = useOffers(selections.map(s => s.offerId))

  const { luxPlusInclusions, bonusInclusions, inclusions, tacticalInclusions } = useOfferInclusions(
    selectedPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const [commonInclusions, exclusiveInclusions] = useMemo(() => {
    const allInclusions = [
      ...bonusInclusions,
      ...inclusions,
      ...tacticalInclusions,
    ]
    return partitionBy(allInclusions, inclusion => isInclusionInAllOtherOffers(inclusion, selectionId, selections, allOffers))
  }, [selectionId, selections, allOffers, bonusInclusions, inclusions, tacticalInclusions])

  const hasAnyInclusions = luxPlusInclusions.length || exclusiveInclusions.length || commonInclusions.length

  return <CellContent>
    {!!hasAnyInclusions && <VerticalSpacer gap={8}>
      <BodyText variant="medium" weight="bold">Inclusions:</BodyText>
      <OfferInclusionsList type="lux-plus" inclusions={luxPlusInclusions} />
      <Group direction="vertical" gap={4}>
        {exclusiveInclusions.map((inclusion) => <OfferInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          isHighlighted
        />)}
        {commonInclusions.map((inclusion) => <OfferInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          isHighlighted={false}
        />)}
      </Group>
    </VerticalSpacer>}
  </CellContent>
}

export default LEHotelExclusiveInclusionsCell
